import React, { useState, useEffect } from 'react';
import { TakeParentSpace } from './styled';

/**
 * @typedef {Object} GeneralTypesOfMediaEnum - Representa os tipos de mídia que serão renderizados por este componente
 * @property {string} IMAGE - Tipo de mídia imagem
 * @property {string} AUDIO - Tipo de mídia áudio
 * @property {string} VIDEO - Tipo de mídia vídeo
 * @property {string} FILE - Tipo de mídia arquivo
 */
const GeneralTypesOfMediaEnum = {
  IMAGE: 'IMAGE',
  AUDIO: 'AUDIO',
  VIDEO: 'VIDEO',
  FILE: 'FILE'
};

/**
 * Recebe um arquivo e lê o seu mime type, retornando uma das chaves do enum `GeneralTypesOfMediaEnum`
 * @param {File} file - O arquivo que deve ser analisado
 * @returns {keyof GeneralTypesOfMediaEnum} - O tipo de mídia correspondente
 */
function getTypeOfMedia(file) {
  const mimeType = file.type;

  if (mimeType.startsWith('image')) return GeneralTypesOfMediaEnum.IMAGE;
  if (mimeType.startsWith('audio')) return GeneralTypesOfMediaEnum.AUDIO;
  if (mimeType.startsWith('video')) return GeneralTypesOfMediaEnum.VIDEO;

  return GeneralTypesOfMediaEnum.FILE;
}

/**
 * Um componente para pré-visualização de arquivos de múltiplos formatos.
 * @param {Object} props - As propriedades do componente
 * @param {(File | null) | Promise<(File | null)>} props.file - O arquivo que deve ser pré-visualizado
 * @param {Function} [props.onFileResolved] - Função de callback chamada quando o arquivo é resolvido
 * @returns {React.ReactNode} - O componente de pré-visualização renderizado
 */
export function FlexMediaPreviewer({ file, onFileResolved }) {
  /**
   * @type {[(File | null), React.Dispatch<React.SetStateAction<(File | null)>>]}
   */
  const [resolvedFile, setResolvedFile] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const resolveFile = async () => {
      const resolved = file instanceof Promise ? await file : file;
      if (isMounted) {
        setResolvedFile(resolved);
        if (onFileResolved) {
          onFileResolved(resolved);
        }
      }
    };

    resolveFile();

    return () => {
      isMounted = false;
    };
  }, [file, onFileResolved]);

  if (!resolvedFile || !('type' in resolvedFile))
    return <div>Nenhum arquivo recebido</div>;

  const mediaType = getTypeOfMedia(resolvedFile);

  return (
    <TakeParentSpace>
      {mediaType === GeneralTypesOfMediaEnum.IMAGE && (
        <img src={URL.createObjectURL(resolvedFile)} alt="Imagem da mídia" />
      )}
      {mediaType === GeneralTypesOfMediaEnum.AUDIO && (
        <audio controls src={URL.createObjectURL(resolvedFile)} />
      )}
      {mediaType === GeneralTypesOfMediaEnum.VIDEO && (
        <video controls src={URL.createObjectURL(resolvedFile)} />
      )}
      {mediaType === GeneralTypesOfMediaEnum.FILE && (
        <iframe src={URL.createObjectURL(resolvedFile)}></iframe>
      )}
    </TakeParentSpace>
  );
}
